// ── Turn panel: step-by-step turn controls ──

import type { GameState, PlayerSlot, TurnPhase } from '../game/state';
import { drawPair, canDraw } from '../game/bag';

export interface TurnPanelCallbacks {
  onChooseTension: (token: number) => void;
  onChooseK: (k: number) => void;
  onConfirmMove: () => void;
  onUndoStrand: () => void;
  onUndoNode: () => void;
}

export function renderTurnPanel(
  container: HTMLElement,
  state: GameState,
  mySlot: PlayerSlot | null,
  callbacks: TurnPanelCallbacks,
  interactionInfo?: { phase: string; strandsDrawn: number; strandsNeeded: number },
): void {
  let panel = container.querySelector('.turn-panel') as HTMLElement | null;
  if (!panel) {
    panel = document.createElement('div');
    panel.className = 'turn-panel';
    container.appendChild(panel);
  }

  // Not my turn or spectating
  if (mySlot === null || state.turn.activePlayer !== mySlot) {
    panel.innerHTML = `
      <div class="turn-panel-content">
        <p class="turn-status">
          ${state.meta.status === 'finished'
            ? ''
            : 'Waiting for opponent...'}
        </p>
      </div>
    `;
    return;
  }

  const firebasePhase = state.turn.phase;
  // The interaction state tracks the local phase more accurately than Firebase
  // (e.g., after placing a node locally, we're in 'drawStrands' but Firebase
  // still says 'placeNode' until the move is committed).
  const localPhase = interactionInfo?.phase;

  if (firebasePhase === 'chooseTension') {
    renderTensionPhase(panel, state, callbacks);
  } else if (firebasePhase === 'chooseK') {
    renderKPhase(panel, state, callbacks);
  } else if (localPhase === 'drawStrand') {
    renderDrawStrandPhase(panel, state, callbacks, interactionInfo);
  } else {
    renderPlaceNodePhase(panel, callbacks, interactionInfo);
  }
}

function renderTensionPhase(
  panel: HTMLElement,
  state: GameState,
  callbacks: TurnPanelCallbacks,
): void {
  const pair = drawPair(state.bag.sequence, state.bag.drawIndex);
  if (!pair) {
    panel.innerHTML = `
      <div class="turn-panel-content">
        <p class="turn-label">Bag Empty</p>
        <p class="turn-hint">No tension tokens remain — the game is over.</p>
      </div>
    `;
    return;
  }

  panel.innerHTML = `
    <div class="turn-panel-content">
      <p class="turn-label">Draw Tension</p>
      <p class="turn-hint">Choose one token to keep as your tension value</p>
      <div class="tension-tokens">
        <button class="token-btn" data-value="${pair.token1}">
          <span class="token-value">${pair.token1}</span>
        </button>
        <button class="token-btn" data-value="${pair.token2}">
          <span class="token-value">${pair.token2}</span>
        </button>
      </div>
    </div>
  `;

  panel.querySelectorAll('.token-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const val = parseInt((btn as HTMLElement).dataset.value!, 10);
      callbacks.onChooseTension(val);
    });
  });
}

function renderKPhase(
  panel: HTMLElement,
  state: GameState,
  callbacks: TurnPanelCallbacks,
): void {
  const t = state.turn.chosenTension ?? 1;

  const buttons = [];
  for (let k = 1; k <= t; k++) {
    buttons.push(
      `<button class="k-btn" data-k="${k}">${k} strand${k > 1 ? 's' : ''}</button>`,
    );
  }

  panel.innerHTML = `
    <div class="turn-panel-content">
      <p class="turn-label">Tension: ${t}</p>
      <p class="turn-hint">How many strands will you draw?</p>
      <div class="k-buttons">
        ${buttons.join('')}
      </div>
    </div>
  `;

  panel.querySelectorAll('.k-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const k = parseInt((btn as HTMLElement).dataset.k!, 10);
      callbacks.onChooseK(k);
    });
  });
}

function renderPlaceNodePhase(
  panel: HTMLElement,
  callbacks: TurnPanelCallbacks,
  interactionInfo?: { phase: string; strandsDrawn: number; strandsNeeded: number },
): void {
  panel.innerHTML = `
    <div class="turn-panel-content">
      <p class="turn-label">Place Node</p>
      <p class="turn-hint">Click inside a region to place your node</p>
    </div>
  `;
}

function renderDrawStrandPhase(
  panel: HTMLElement,
  state: GameState,
  callbacks: TurnPanelCallbacks,
  interactionInfo?: { phase: string; strandsDrawn: number; strandsNeeded: number },
): void {
  const drawn = interactionInfo?.strandsDrawn ?? 0;
  const needed = interactionInfo?.strandsNeeded ?? (state.turn.chosenK ?? 1);

  panel.innerHTML = `
    <div class="turn-panel-content">
      <p class="turn-label">Draw Strand <span class="strand-count">(${drawn}/${needed})</span></p>
      <p class="turn-hint">Drag from the board to a highlighted vertex</p>
      <div class="turn-undo-row">
        ${drawn > 0 ? '<button class="btn btn-link" id="btn-undo-strand">Undo last strand</button>' : ''}
        <button class="btn btn-link" id="btn-undo-node">Reposition node</button>
      </div>
    </div>
  `;

  panel.querySelector('#btn-undo-strand')?.addEventListener('click', () => callbacks.onUndoStrand());
  panel.querySelector('#btn-undo-node')?.addEventListener('click', () => callbacks.onUndoNode());
}

/** Render the pie rule prompt */
export function renderPieRule(
  container: HTMLElement,
  callbacks: { onSwap: () => void; onDecline: () => void },
): void {
  let panel = container.querySelector('.turn-panel') as HTMLElement | null;
  if (!panel) {
    panel = document.createElement('div');
    panel.className = 'turn-panel';
    container.appendChild(panel);
  }

  panel.innerHTML = `
    <div class="turn-panel-content pie-rule">
      <p class="turn-label">Opening Exchange</p>
      <p class="turn-hint">Your opponent has made the first move. Would you like to swap colors?</p>
      <div class="pie-buttons">
        <button class="btn btn-primary" id="btn-swap">Swap Colors</button>
        <button class="btn btn-secondary" id="btn-decline">Keep Colors</button>
      </div>
    </div>
  `;

  panel.querySelector('#btn-swap')!.addEventListener('click', callbacks.onSwap);
  panel.querySelector('#btn-decline')!.addEventListener('click', callbacks.onDecline);
}
